import { Popover } from "@base-ui-components/react/popover"
import { Tooltip } from "@base-ui-components/react/tooltip"
import { ChevronDown, Eye, EyeOff, Loader2 } from "lucide-react"
import { useEffect, useId, useRef, useState } from "react"
import { usePortalContainer } from "../hooks/usePortalContainer"
import type { TaskPullRequestAutomation } from "../persistence/records"
import { pullRequestAutomationStatus } from "../utils/pullRequestAutomation"

type AutomationProps = {
    automation?: TaskPullRequestAutomation
    disabledReason?: string
    onChange: (enabled: boolean) => Promise<string | null>
    onRetry?: () => Promise<string | null>
}

export function PullRequestAutomationControls({ automation, disabledReason, onChange, onRetry }: AutomationProps) {
    const portalContainer = usePortalContainer()
    const saving = useRef(false)
    const [pending, setPending] = useState<"watch" | "retry" | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [detailsOpen, setDetailsOpen] = useState(false)
    // Opening also resolves the themed portal container after its first mount.
    const [tooltipOpen, setTooltipOpen] = useState(false)
    const detailsId = useId()
    const attention = automation?.phase === "attention"
    const ready = attention && automation.hold?.reason === "ready"
    const holdDetail = attention ? automation?.hold?.detail?.trim() : undefined
    const hasDetails = Boolean(error || (attention && !ready && (holdDetail || onRetry)))
    useEffect(() => setDetailsOpen(false), [holdDetail, automation?.linkId, automation?.hold?.reason])
    const running = automation?.phase === "running"
    const status = pending ? "Saving…" : automation ? pullRequestAutomationStatus(automation) : "Watch off"
    const runningNote = "Already-running work may finish. Stopping cancels queued repairs."
    const tooltip = `Watch PR: ${automation ? "On" : "Off"}. ${
        disabledReason ??
        (pending
            ? "Saving…"
            : automation
              ? `${status}. Click to turn off.${running ? ` ${runningNote}` : ""}`
              : "Click to turn on watching and repair blockers in the background.")
    }`
    const run = async (stage: "watch" | "retry", operation: () => Promise<string | null>) => {
        if (saving.current) return
        saving.current = true
        setPending(stage)
        setError(null)
        try {
            const message = await operation()
            setError(message)
            setDetailsOpen(Boolean(message))
        } catch (cause) {
            setError(cause instanceof Error ? cause.message : "Could not save PR settings.")
            setDetailsOpen(true)
        } finally {
            saving.current = false
            setPending(null)
        }
    }
    return (
        <span className="inline-flex shrink-0 items-center" role="group" aria-label="PR watch">
            <Tooltip.Root open={tooltipOpen} onOpenChange={setTooltipOpen} delay={200}>
                <Tooltip.Trigger
                    type="button"
                    className="btn flex h-6 items-center justify-center gap-1.5 whitespace-nowrap border border-border px-1.5 text-[11px] font-medium text-muted hover:bg-base-200 hover:text-base-content aria-pressed:border-primary aria-pressed:bg-primary aria-pressed:text-primary-content aria-pressed:hover:bg-primary aria-pressed:hover:text-primary-content disabled:cursor-default disabled:opacity-50"
                    aria-label="Watch PR"
                    aria-pressed={Boolean(automation)}
                    aria-busy={Boolean(pending)}
                    disabled={Boolean(pending || disabledReason)}
                    title={tooltip}
                    onClick={() => void run("watch", () => onChange(!automation))}
                >
                    {pending ? <Loader2 size={12} className="animate-spin" /> : automation ? <Eye size={12} /> : <EyeOff size={12} />}
                    <span>Watch {automation ? "on" : "off"}</span>
                </Tooltip.Trigger>
                <Tooltip.Portal container={portalContainer}>
                    <Tooltip.Positioner side="top" sideOffset={6} collisionPadding={8}>
                        <Tooltip.Popup
                            role="tooltip"
                            className="z-50 max-w-[min(20rem,calc(100vw-1rem))] border border-border bg-base-200 px-3 py-2 text-xs leading-relaxed text-base-content shadow-lg"
                        >
                            {tooltip}
                        </Tooltip.Popup>
                    </Tooltip.Positioner>
                </Tooltip.Portal>
            </Tooltip.Root>
            <span className="sr-only" role="status">
                {status}
            </span>
            <Popover.Root open={hasDetails && detailsOpen} onOpenChange={setDetailsOpen}>
                {hasDetails && (
                    <Popover.Trigger
                        className="btn flex h-6 w-4 items-center justify-center text-warning hover:bg-base-200"
                        aria-label="PR automation details"
                        aria-controls={detailsId}
                        title={error ?? status}
                    >
                        <ChevronDown size={10} />
                    </Popover.Trigger>
                )}
                <Popover.Portal container={portalContainer}>
                    <Popover.Positioner side="bottom" align="end" sideOffset={6}>
                        <Popover.Popup
                            id={detailsId}
                            aria-label="PR watch details"
                            className="z-50 w-72 max-w-[calc(100vw-1rem)] border border-border bg-base-100 p-3 text-xs text-base-content shadow-lg outline-none"
                        >
                            <div className="flex items-center justify-between gap-2">
                                <span className="font-medium">{status}</span>
                                {attention && !ready && onRetry && (
                                    <button
                                        type="button"
                                        className="btn min-h-7 shrink-0 px-2 text-[11px] hover:bg-base-200 disabled:opacity-50"
                                        aria-label="Retry PR watch"
                                        disabled={Boolean(pending || disabledReason)}
                                        onClick={() => void run("retry", onRetry)}
                                    >
                                        Retry
                                    </button>
                                )}
                            </div>
                            {running && <p className="mt-2 text-[11px] text-muted">{runningNote}</p>}
                            {holdDetail && (
                                <div
                                    className="mt-2 max-h-40 overflow-y-auto overscroll-contain whitespace-pre-wrap break-words leading-relaxed"
                                    role="region"
                                    aria-label="PR automation blocker"
                                    tabIndex={0}
                                >
                                    {holdDetail}
                                </div>
                            )}
                            {error && (
                                <p className="mt-2 max-h-40 overflow-y-auto whitespace-pre-wrap break-words" role="alert">
                                    {error}
                                </p>
                            )}
                        </Popover.Popup>
                    </Popover.Positioner>
                </Popover.Portal>
            </Popover.Root>
        </span>
    )
}
